import i18next, { InitOptions } from 'i18next'
import Backend from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { env } from 'config'
import { ALL_LANGUAGES, getSupportedLanguages } from './flavor'

const TRANSLATIONS_ROOT = `${process.env.PUBLIC_URL}/locales`
const FLAVOR = env.productFlavor

export const DEFAULT_LANGUAGE = 'en'
export const DEFAULT_NAMESPACE = 'common'

export const languages = [...ALL_LANGUAGES]
export const supportedLanguages = getSupportedLanguages(languages, FLAVOR)

const VERSION = process.env.REACT_APP_VERSION || Date.now().toString()

const withQuery = (url: string) =>
  `${url}${url.includes('?') ? '&' : '?'}v=${VERSION}`

const translationRequestCache = new Map<
  string,
  Promise<Record<string, unknown> | null>
>()

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const mergeDeep = (
  base: Record<string, unknown>,
  override: Record<string, unknown>
): Record<string, unknown> => {
  const output = { ...base }

  Object.entries(override).forEach(([key, value]) => {
    const currentValue = output[key]

    if (isRecord(currentValue) && isRecord(value)) {
      output[key] = mergeDeep(currentValue, value)
      return
    }

    output[key] = value
  })

  return output
}

const loadTranslationsPath = (
  languagesToLoad: readonly string[],
  namespacesToLoad: readonly string[]
) => `${TRANSLATIONS_ROOT}/${languagesToLoad[0]}/${namespacesToLoad[0]}.json`

const i18nOptions: InitOptions = {
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: supportedLanguages.map(({ code }) => code),
  defaultNS: DEFAULT_NAMESPACE,
  fallbackNS: [DEFAULT_NAMESPACE],
  load: 'languageOnly',

  interpolation: {
    escapeValue: false,
  },

  detection: {
    order: ['cookie', 'navigator'],
    caches: ['cookie'],
  },

  backend: {
    loadPath: loadTranslationsPath,

    request: (
      _options: unknown,
      url: string,
      _payload: unknown,
      callback: (
        error: unknown,
        response: { status: number; data: string }
      ) => void
    ) => {
      const parsedUrl = new URL(url, window.location.origin)
      const pathParts = parsedUrl.pathname.split('/')

      const namespace =
        pathParts[pathParts.length - 1]?.replace('.json', '') ||
        DEFAULT_NAMESPACE

      const language = pathParts[pathParts.length - 2] || DEFAULT_LANGUAGE

      const search = parsedUrl.search

      const commonNamespaceUrl = withQuery(
        `${TRANSLATIONS_ROOT}/common/${language}/${namespace}.json${search}`
      )

      const flavorNamespaceUrl = withQuery(
        `${TRANSLATIONS_ROOT}/${language}/${namespace}.json${search}`
      )

      const legacyFlavorNamespaceUrl = withQuery(
        `${TRANSLATIONS_ROOT}/${FLAVOR}/${language}/${namespace}.json${search}`
      )

      const commonDefaultUrl = withQuery(
        `${TRANSLATIONS_ROOT}/common/${language}/${DEFAULT_NAMESPACE}.json${search}`
      )

      const fetchJson = (targetUrl: string) => {
        const cachedRequest = translationRequestCache.get(targetUrl)
        if (cachedRequest) return cachedRequest

        const requestPromise = fetch(targetUrl)
          .then(async response => {
            if (!response.ok) return null
            return (await response.json()) as Record<string, unknown>
          })
          .catch(() => null)

        translationRequestCache.set(targetUrl, requestPromise)
        return requestPromise
      }

      Promise.all([
        fetchJson(commonNamespaceUrl),
        fetchJson(flavorNamespaceUrl),
        fetchJson(legacyFlavorNamespaceUrl),
      ])
        .then(
          async ([commonNamespace, flavorNamespace, legacyFlavorNamespace]) => {
            const selectedFlavorNamespace =
              flavorNamespace || legacyFlavorNamespace

            const mergedNamespace =
              commonNamespace || selectedFlavorNamespace
                ? mergeDeep(
                    commonNamespace || {},
                    selectedFlavorNamespace || {}
                  )
                : null

            if (mergedNamespace) {
              callback(null, {
                status: 200,
                data: JSON.stringify(mergedNamespace),
              })
              return
            }

            const commonDefault = await fetchJson(commonDefaultUrl)

            if (commonDefault) {
              callback(null, {
                status: 200,
                data: JSON.stringify(commonDefault),
              })
              return
            }

            callback(null, { status: 404, data: '{}' })
          }
        )
        .catch(error => callback(error, { status: 500, data: '{}' }))
    },
  },
}

const i18n = {
  init: () =>
    i18next
      .use(Backend)
      .use(initReactI18next)
      .use(LanguageDetector)
      .init(i18nOptions),
}

export default i18n
